import React, { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { 
  FileText, 
  Download, 
  ExternalLink, 
  Settings, 
  Save, 
  RefreshCw,
  Crown,
  Star,
  User,
  Building2,
  Check,
  AlertCircle,
  Plus,
  Trash2,
  GripVertical,
  Link as LinkIcon,
  Users,
  UserCheck,
  UserX,
  AlertTriangle,
  History,
  Clock,
  X,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { format } from 'date-fns';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Documentation Hub Component with Version History
export const DocumentationHub = () => {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);
  const [versionNotes, setVersionNotes] = useState('');
  const [docVersions, setDocVersions] = useState({}); // Store versions per document
  const [openDropdown, setOpenDropdown] = useState(null); // Which dropdown is open
  const [latestVersions, setLatestVersions] = useState({}); // Store latest version per document
  const [exportingAnalytics, setExportingAnalytics] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  
  // Export functions
  const handleExportInvestorAnalytics = async () => {
    try {
      setExportingAnalytics(true);
      toast.loading('Generating investor analytics...');
      const response = await axios.get(`${API_BASE}/api/admin/export/investor-analytics`);
      const data = response.data;
      
      // Create a formatted JSON file
      const jsonStr = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `investor_analytics_${format(new Date(), 'yyyy-MM-dd')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.dismiss();
      toast.success('Investor analytics exported!');
    } catch (err) {
      toast.dismiss();
      toast.error('Export failed');
    } finally {
      setExportingAnalytics(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExportingCSV(true);
      const response = await axios.get(`${API_BASE}/api/admin/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `uncloned_users_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Users exported to CSV');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExportingCSV(false);
    }
  };
  
  // Base document definitions (version is fetched from DB)
  const baseDocuments = [
    {
      id: 'b2b-onboarding',
      title: 'B2B Client Onboarding Guide',
      description: 'Step-by-step instructions for adding B2B clients, email templates, and DNS setup',
      url: '/b2b-onboarding-guide.html',
      pages: 8,
      defaultVersion: '1.0',
      category: 'Operations',
    },
    {
      id: 'b2b-licensing',
      title: 'B2B White-Label Licensing Guide',
      description: 'Complete guide to white-label portal setup, client management, and feature toggles',
      url: '/b2b-licensing-guide.html',
      pages: 12,
      defaultVersion: '1.0',
      category: 'Operations',
    },
    {
      id: 'investor-deck',
      title: 'Investor Deck',
      description: '149-page comprehensive presentation with all user, admin, B2B features, marketing automation, referral system, technical deep dives, and glossary. First B2B client signed ($1,500).',
      url: '/investor-deck.html',
      pages: 149,
      defaultVersion: '7.7',
      category: 'Business',
    },
    {
      id: 'mobile-app-guide',
      title: 'Mobile App Development Guide',
      description: 'Complete guide for building native iOS and Android apps with React Native',
      url: '/mobile-app-guide.html',
      pages: 8,
      defaultVersion: '1.0',
      category: 'Technical',
    },
    {
      id: 'development-cost',
      title: 'Development Cost Analysis',
      description: 'Detailed breakdown of what it would cost to build this platform from scratch ($900,825 total, 4,074 dev hours)',
      url: '/development-cost-analysis.html',
      pages: 9,
      defaultVersion: '7.7',
      category: 'Business',
    },
    {
      id: 'monetization-guide',
      title: 'In-App Monetization Guide',
      description: 'Premium upgrades and add-ons strategy: prompt packs, challenges, export themes, AI subscriptions, and founding member positioning',
      url: '/monetization-guide.html',
      pages: 11,
      defaultVersion: '1.0',
      category: 'Business',
    },
    {
      id: 'stripe-guide',
      title: 'Stripe Integration Guide',
      description: 'Complete documentation for payment integration, webhooks, and subscription management',
      url: '/stripe-guide.html',
      pages: 6,
      defaultVersion: '1.0',
      category: 'Technical',
    },
    {
      id: 'branding-guide',
      title: 'Brand & Styling Guide',
      description: 'Design system documentation including colors, typography, components, and B2B theming',
      url: '/branding-guide.html',
      pages: 5,
      defaultVersion: '1.0',
      category: 'Design',
    },
    {
      id: 'admin-guide',
      title: 'Admin Dashboard Guide',
      description: 'Complete guide to all 13 admin tabs, email marketing, and subscription management',
      url: '/admin-guide.html',
      pages: 5,
      defaultVersion: '1.1',
      category: 'Operations',
    },
    {
      id: 'release-notes',
      title: 'Deployment Release Notes',
      description: 'All features and changes in the latest deployment including Stripe, Promos, and Tiers',
      url: '/release-notes.html',
      pages: 5,
      defaultVersion: '2.0',
      category: 'Technical',
    },
  ];
  
  // Merge base documents with latest versions from DB
  const documents = baseDocuments.map(doc => ({
    ...doc,
    version: latestVersions[doc.id] || doc.defaultVersion
  }));

  // Fetch latest versions for all documents on mount
  useEffect(() => {
    const fetchLatestVersions = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/admin/documents/all`, {
          withCredentials: true
        });
        const versionsMap = {};
        (response.data.documents || []).forEach(doc => {
          versionsMap[doc.document_id] = doc.latest_version;
        });
        setLatestVersions(versionsMap);
      } catch (error) {
        console.error('Failed to fetch latest versions:', error);
      }
    };
    fetchLatestVersions();
  }, []);

  const handleView = (url) => {
    window.open(url, '_blank');
  };

  const handleDownload = (url, title) => {
    // Open in new tab with print dialog
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => {
        setTimeout(() => {
          win.print();
        }, 500);
      };
    }
  };

  const fetchVersions = async (docId) => {
    setLoadingVersions(true);
    try {
      const response = await axios.get(`${API_BASE}/api/admin/documents/versions/${docId}`);
      setVersions(response.data.versions || []);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  // Fetch versions for dropdown when it opens
  const fetchVersionsForDropdown = async (docId) => {
    if (docVersions[docId]) return; // Already loaded
    try {
      const response = await axios.get(`${API_BASE}/api/admin/documents/versions/${docId}`);
      setDocVersions(prev => ({
        ...prev,
        [docId]: response.data.versions || []
      }));
    } catch (error) {
      console.error('Failed to fetch versions for dropdown:', error);
      setDocVersions(prev => ({
        ...prev,
        [docId]: []
      }));
    }
  };

  const toggleDropdown = async (docId) => {
    if (openDropdown === docId) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(docId);
      await fetchVersionsForDropdown(docId);
    }
  };

  const viewArchivedVersion = async (docId, versionId) => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/documents/versions/${docId}/${versionId}`);
      const blob = new Blob([response.data.content], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setOpenDropdown(null);
    } catch (error) {
      toast.error('Failed to load version');
    }
  };

  const openVersionHistory = (doc) => {
    setSelectedDoc(doc);
    setShowVersionModal(true);
    fetchVersions(doc.id);
  };

  const saveCurrentVersion = async () => {
    if (!selectedDoc) return;
    
    setSavingVersion(true);
    try {
      // Fetch the current HTML content
      const response = await fetch(selectedDoc.url);
      const content = await response.text();
      
      await axios.post(`${API_BASE}/api/admin/documents/versions`, {
        document_id: selectedDoc.id,
        title: selectedDoc.title,
        version_number: selectedDoc.version,
        description: selectedDoc.description,
        content: content,
        pages: selectedDoc.pages,
        notes: versionNotes || `Version ${selectedDoc.version} snapshot`
      });
      
      toast.success(`Version ${selectedDoc.version} saved to history`);
      setVersionNotes('');
      fetchVersions(selectedDoc.id);
      
      // Refresh latest versions to update UI
      try {
        const allDocsResponse = await axios.get(`${API_BASE}/api/admin/documents/all`, {
          withCredentials: true
        });
        const versionsMap = {};
        (allDocsResponse.data.documents || []).forEach(doc => {
          versionsMap[doc.document_id] = doc.latest_version;
        });
        setLatestVersions(versionsMap);
      } catch (e) {
        console.error('Failed to refresh versions:', e);
      }
    } catch (error) {
      if (error.response?.data?.existing_version) {
        toast.info(`This exact content already exists as version ${error.response.data.existing_version}`);
      } else {
        toast.error('Failed to save version');
      }
    } finally {
      setSavingVersion(false);
    }
  };

  const viewVersion = (version) => {
    // Create a blob URL for the stored HTML content and open it
    const blob = new Blob([version.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const deleteVersion = async (versionId) => {
    if (!selectedDoc) return;
    
    if (!window.confirm('Are you sure you want to delete this version?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/admin/documents/versions/${selectedDoc.id}/${versionId}`);
      toast.success('Version deleted');
      fetchVersions(selectedDoc.id);
    } catch (error) {
      toast.error('Failed to delete version');
    }
  };

  const categoryColors = {
    Business: 'from-purple-500 to-pink-500',
    Technical: 'from-blue-500 to-cyan-500',
    Design: 'from-orange-500 to-yellow-500',
    Operations: 'from-green-500 to-emerald-500',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#df1283]" />
            Documentation Hub
          </h2>
          <p className="text-sm text-[#FDF2F8]/60 mt-1">
            Access all PDF guides and documentation with version history
          </p>
        </div>
      </div>

      {/* Export Data Section */}
      <div className="p-6 bg-gradient-to-br from-[#FF7901]/20 to-[#df1283]/20 border border-[#FF7901]/40 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Download className="w-5 h-5 text-[#FF7901]" />
          Export Data
        </h3>
        <p className="text-sm text-[#FDF2F8]/70 mb-4">
          Export comprehensive analytics for investor deck updates and business reporting.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button 
            onClick={handleExportInvestorAnalytics}
            disabled={exportingAnalytics}
            className="bg-gradient-to-r from-[#FF7901] to-[#df1283] text-white hover:opacity-90"
            data-testid="export-investor-analytics-docs-btn"
          >
            {exportingAnalytics ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export Investor Analytics (JSON)
          </Button>
          <Button 
            onClick={handleExportCSV}
            disabled={exportingCSV}
            variant="outline"
            className="border-[#0FFFE2] text-[#0FFFE2] hover:bg-[#0FFFE2]/20"
            data-testid="export-users-csv-docs-btn"
          >
            {exportingCSV ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export Users (CSV)
          </Button>
        </div>
        <p className="text-xs text-[#FDF2F8]/50 mt-3">
          Investor Analytics includes: user metrics, signup trends, traffic sources, engagement data, feature usage, B2B stats, and marketing performance.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-5 hover:border-[#df1283]/50 transition-all group"
          >
            {/* Category Badge */}
            <div className="flex items-center justify-between mb-3">
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-gradient-to-r ${categoryColors[doc.category]} text-white`}>
                {doc.category}
              </span>
              <span className="text-xs text-[#FDF2F8]/40">v{doc.version}</span>
            </div>

            {/* Title & Description */}
            <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#df1283] transition-colors">
              {doc.title}
            </h3>
            <p className="text-sm text-[#FDF2F8]/60 mb-4 line-clamp-2">
              {doc.description}
            </p>

            {/* Meta */}
            <div className="flex items-center gap-4 text-xs text-[#FDF2F8]/40 mb-4">
              <span>{doc.pages} pages</span>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              {/* Main Row: View Current + Download */}
              <div className="flex gap-2">
                <Button
                  onClick={() => handleView(doc.url)}
                  size="sm"
                  className="flex-1 bg-[#4A0A66] hover:bg-[#5A1A76] text-white"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  View Current (v{doc.version})
                </Button>
                <Button
                  onClick={() => handleDownload(doc.url, doc.title)}
                  size="sm"
                  variant="outline"
                  className="border-[#df1283] text-[#df1283] hover:bg-[#df1283]/10"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Version Dropdown Row */}
              <div className="relative">
                <Button
                  onClick={() => toggleDropdown(doc.id)}
                  size="sm"
                  variant="outline"
                  className="w-full border-[#FF7901] text-[#FF7901] hover:bg-[#FF7901]/10 justify-between"
                >
                  <span className="flex items-center gap-1">
                    <History className="w-4 h-4" />
                    Previous Versions
                  </span>
                  <span className="text-xs">▼</span>
                </Button>
                
                {/* Dropdown Menu */}
                {openDropdown === doc.id && (
                  <div className="absolute z-50 mt-1 w-full bg-[#1a0527] border border-[#4A0A66] rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {!docVersions[doc.id] ? (
                      <div className="p-3 text-center text-[#FDF2F8]/60 text-sm">
                        <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1" />
                        Loading...
                      </div>
                    ) : docVersions[doc.id].length === 0 ? (
                      <div className="p-3 text-center text-[#FDF2F8]/60 text-sm">
                        No saved versions yet
                      </div>
                    ) : (
                      <>
                        {docVersions[doc.id].map((v) => (
                          <button
                            key={v.version_id}
                            onClick={() => viewArchivedVersion(doc.id, v.version_id)}
                            className="w-full px-3 py-2 text-left hover:bg-[#4A0A66]/50 transition-colors border-b border-[#4A0A66]/30 last:border-0"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-white font-medium text-sm">v{v.version_number}</span>
                              <span className="text-xs text-[#FDF2F8]/40">{v.pages} pages</span>
                            </div>
                            <div className="text-xs text-[#FDF2F8]/50 mt-0.5">
                              {new Date(v.created_at).toLocaleDateString()} - {v.notes?.slice(0, 30) || 'No notes'}
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                    {/* Save New Version Link */}
                    <button
                      onClick={() => {
                        setOpenDropdown(null);
                        openVersionHistory(doc);
                      }}
                      className="w-full px-3 py-2 text-left bg-[#4A0A66]/30 hover:bg-[#4A0A66]/50 transition-colors text-[#df1283] text-sm font-medium"
                    >
                      + Save Current as New Version
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PDF Template Guide Link */}
      <div className="mt-6 p-4 bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg">
        <p className="text-sm text-[#FDF2F8]/70">
          <strong className="text-[#FF7901]">For Developers:</strong> When creating new PDF documents, 
          follow the formatting guidelines in <code className="px-1 py-0.5 bg-[#1a0527] rounded text-[#FF7901]">/app/docs/PDF_TEMPLATE_GUIDE.md</code>
        </p>
      </div>

      {/* Version History Modal */}
      {showVersionModal && selectedDoc && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-[#4A0A66]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-[#FF7901]" />
                    Version History
                  </h3>
                  <p className="text-sm text-[#FDF2F8]/60 mt-1">{selectedDoc.title}</p>
                </div>
                <Button
                  onClick={() => setShowVersionModal(false)}
                  size="sm"
                  variant="ghost"
                  className="text-[#FDF2F8]/60 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Save Current Version */}
            <div className="p-4 border-b border-[#4A0A66] bg-[#4A0A66]/20">
              <div className="flex gap-3">
                <Input
                  placeholder="Add notes for this version (optional)"
                  value={versionNotes}
                  onChange={(e) => setVersionNotes(e.target.value)}
                  className="flex-1 bg-[#1a0527] border-[#4A0A66] text-white"
                />
                <Button
                  onClick={saveCurrentVersion}
                  disabled={savingVersion}
                  className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90 text-white"
                >
                  {savingVersion ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Current (v{selectedDoc.version})
                </Button>
              </div>
            </div>

            {/* Version List */}
            <div className="p-4 overflow-y-auto max-h-[400px]">
              {loadingVersions ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 text-[#df1283] animate-spin mx-auto" />
                  <p className="text-[#FDF2F8]/60 mt-2">Loading versions...</p>
                </div>
              ) : versions.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-[#4A0A66] mx-auto mb-3" />
                  <p className="text-[#FDF2F8]/60">No saved versions yet</p>
                  <p className="text-sm text-[#FDF2F8]/40 mt-1">
                    Click "Save Current" to archive this version
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {versions.map((version) => (
                    <div
                      key={version.version_id}
                      className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4 hover:border-[#df1283]/30 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">
                              v{version.version_number}
                            </span>
                            <span className="text-xs px-2 py-0.5 bg-[#4A0A66] rounded-full text-[#FDF2F8]/60">
                              {version.pages} pages
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#FDF2F8]/40 mt-1">
                            <Clock className="w-3 h-3" />
                            {new Date(version.created_at).toLocaleString()}
                          </div>
                          {version.notes && (
                            <p className="text-sm text-[#FDF2F8]/60 mt-2">{version.notes}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={async () => {
                              // Fetch full version with content
                              try {
                                const res = await axios.get(
                                  `${API_BASE}/api/admin/documents/versions/${selectedDoc.id}/${version.version_id}`
                                );
                                viewVersion(res.data);
                              } catch (err) {
                                toast.error('Failed to load version');
                              }
                            }}
                            size="sm"
                            variant="outline"
                            className="border-[#4A0A66] text-[#FDF2F8]/70 hover:text-white hover:border-[#df1283]"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => deleteVersion(version.version_id)}
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tier Labels Settings Component
export const TierLabelsSettings = () => {
  const [labels, setLabels] = useState({
    founding_member: 'Founding Member',
    premium: 'Premium',
    free: 'Free',
    enterprise: 'Enterprise',
  });
  const [originalLabels, setOriginalLabels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchTierLabels();
  }, []);

  useEffect(() => {
    if (originalLabels) {
      const changed = Object.keys(labels).some(key => labels[key] !== originalLabels[key]);
      setHasChanges(changed);
    }
  }, [labels, originalLabels]);

  const fetchTierLabels = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/settings/tier-labels`);
      if (response.data.labels) {
        setLabels(response.data.labels);
        setOriginalLabels(response.data.labels);
      }
    } catch (error) {
      // Use defaults if not found
      setOriginalLabels(labels);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/admin/settings/tier-labels`, { labels });
      setOriginalLabels(labels);
      setHasChanges(false);
      toast.success('Tier labels saved successfully');
    } catch (error) {
      toast.error('Failed to save tier labels');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLabels({
      founding_member: 'Founding Member',
      premium: 'Premium',
      free: 'Free',
      enterprise: 'Enterprise',
    });
  };

  const tierIcons = {
    founding_member: { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
    premium: { icon: Star, color: 'text-purple-400', bg: 'bg-purple-500/20' },
    free: { icon: User, color: 'text-gray-400', bg: 'bg-gray-500/20' },
    enterprise: { icon: Building2, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 text-[#df1283] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#df1283]" />
            Membership Tier Labels
          </h3>
          <p className="text-sm text-[#FDF2F8]/60 mt-1">
            Customize the display names for each membership tier
          </p>
        </div>
        {hasChanges && (
          <span className="flex items-center gap-1 text-xs text-yellow-400">
            <AlertCircle className="w-4 h-4" />
            Unsaved changes
          </span>
        )}
      </div>

      <div className="grid gap-4">
        {Object.entries(labels).map(([key, value]) => {
          const tierInfo = tierIcons[key];
          const Icon = tierInfo.icon;
          
          return (
            <div
              key={key}
              className="flex items-center gap-4 p-4 bg-[#2D063E]/50 border border-[#4A0A66] rounded-lg"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tierInfo.bg}`}>
                <Icon className={`w-5 h-5 ${tierInfo.color}`} />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-[#FDF2F8]/50 mb-1 uppercase tracking-wide">
                  {key.replace('_', ' ')} Display Label
                </label>
                <Input
                  value={value}
                  onChange={(e) => setLabels(prev => ({ ...prev, [key]: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white focus:border-[#df1283]"
                  placeholder={`Enter ${key} label...`}
                />
              </div>
              <div className="text-sm text-[#FDF2F8]/40">
                Preview: <span className={`font-medium ${tierInfo.color}`}>{value || key}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-[#4A0A66]">
        <Button
          onClick={handleReset}
          variant="ghost"
          className="text-[#FDF2F8]/60 hover:text-white"
        >
          Reset to Defaults
        </Button>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white hover:opacity-90"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Usage Note */}
      <div className="p-4 bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg">
        <p className="text-sm text-[#FDF2F8]/70">
          <strong className="text-[#0FFFE2]">Note:</strong> These labels are used throughout the app including 
          user badges, admin tables, subscription management, and promotional targeting. Changes apply globally.
        </p>
      </div>
    </div>
  );
};

// Signup Settings Component - Control default tier for new users
export const SignupSettings = () => {
  const [settings, setSettings] = useState({
    default_tier: 'free',
    founding_member_enabled: false,
    founding_member_end_date: '',
  });
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [liveStatus, setLiveStatus] = useState(null);

  // Default expiration: August 12, 2026 at 12:00 AM Eastern Time
  // Format for datetime-local input: YYYY-MM-DDTHH:MM
  const DEFAULT_END_DATE = '2026-08-12T00:00';

  // Helper: Convert technical date to user-facing date (subtract 2 days)
  // Technical: Aug 12 at 12:00 AM -> User sees: Aug 10 at midnight EST
  const formatUserFacingDate = (technicalDate) => {
    if (!technicalDate) return null;
    try {
      const date = new Date(technicalDate);
      // Subtract 2 days for user-facing display
      date.setDate(date.getDate() - 2);
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      }) + ' at midnight EST';
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    fetchSignupSettings();
    fetchLiveStatus();
  }, []);

  useEffect(() => {
    if (originalSettings) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  const fetchLiveStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/auth/founding-member-status`);
      setLiveStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch live status:', error);
    }
  };

  const fetchSignupSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/settings/signup`, { withCredentials: true });
      if (response.data.settings) {
        const fetchedSettings = {
          ...response.data.settings,
          founding_member_end_date: response.data.settings.founding_member_end_date || DEFAULT_END_DATE,
        };
        setSettings(fetchedSettings);
        setOriginalSettings(fetchedSettings);
      } else {
        // Set defaults - founding member disabled by default
        const defaultSettings = {
          default_tier: 'free',
          founding_member_enabled: false,
          founding_member_end_date: DEFAULT_END_DATE,
        };
        setSettings(defaultSettings);
        setOriginalSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Failed to fetch signup settings:', error);
      toast.error('Failed to load signup settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saveSettings = {
        ...settings,
        founding_member_end_date: settings.founding_member_end_date || null,
      };
      await axios.put(`${API_BASE}/api/admin/settings/signup`, { settings: saveSettings }, { withCredentials: true });
      setOriginalSettings(settings);
      toast.success('Signup settings saved successfully!');
      // Refresh live status after save
      fetchLiveStatus();
    } catch (error) {
      console.error('Failed to save signup settings:', error);
      toast.error('Failed to save signup settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-[#df1283]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0FFFE2] to-teal-600 flex items-center justify-center">
          <UserCheck className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">New User Signup Settings</h3>
          <p className="text-sm text-[#FDF2F8]/60">Control what membership tier new users receive when they sign up</p>
        </div>
      </div>

      {/* Live Status Banner */}
      {liveStatus && (
        <div className={`rounded-xl p-4 border ${liveStatus.available 
          ? 'bg-gradient-to-r from-green-500/20 to-emerald-600/20 border-green-500/50' 
          : 'bg-gradient-to-r from-red-500/20 to-rose-600/20 border-red-500/50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${liveStatus.available ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              <div>
                <p className={`font-semibold ${liveStatus.available ? 'text-green-400' : 'text-red-400'}`}>
                  {liveStatus.available ? 'Founding Member is LIVE' : 'Founding Member is DISABLED'}
                </p>
                <p className="text-sm text-white/60">
                  {liveStatus.available && liveStatus.days_remaining !== null
                    ? `${liveStatus.days_remaining} days remaining until auto-expiration`
                    : liveStatus.message}
                </p>
              </div>
            </div>
            {liveStatus.available && liveStatus.expiration_date && (
              <div className="text-right">
                <p className="text-xs text-white/40">Auto-expires</p>
                <p className="text-sm text-yellow-400 font-medium">
                  {(() => {
                    // Parse date and display without timezone conversion
                    const d = new Date(liveStatus.expiration_date);
                    const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'America/New_York' });
                    const day = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'America/New_York' });
                    const year = d.toLocaleDateString('en-US', { year: 'numeric', timeZone: 'America/New_York' });
                    return `${month} ${day}, ${year} at 12:00 AM EST`;
                  })()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Founding Member Toggle */}
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center flex-shrink-0">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-white font-semibold">Founding Member Signups</h4>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.founding_member_enabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, founding_member_enabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-[#4A0A66] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-[#df1283] peer-checked:to-[#FF7901]"></div>
              </label>
            </div>
            <p className="text-sm text-[#FDF2F8]/60 mb-4">
              {settings.founding_member_enabled 
                ? 'New users will receive Founding Member status with lifetime access to all features.'
                : 'New users will receive the default tier selected below.'}
            </p>
            
            {settings.founding_member_enabled && (
              <div className="mt-4 pt-4 border-t border-[#4A0A66]/50">
                <label className="text-sm text-[#FDF2F8]/70 block mb-2">
                  Auto-Expiration Date & Time (Eastern Time)
                </label>
                <Input
                  type="datetime-local"
                  value={settings.founding_member_end_date}
                  onChange={(e) => setSettings(prev => ({ ...prev, founding_member_end_date: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white focus:border-[#df1283] max-w-xs [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                  style={{ colorScheme: 'dark' }}
                />
                <p className="text-xs text-[#FDF2F8]/40 mt-1">
                  Founding Member will automatically disappear from pricing at this time.
                </p>
                <p className="text-xs text-yellow-500/70 mt-1">
                  Default: August 12, 2026 at 12:00 AM Eastern (midnight)
                </p>
                {settings.founding_member_end_date && (
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, founding_member_end_date: '' }))}
                    className="text-xs text-red-400 hover:text-red-300 mt-2 underline"
                  >
                    Remove expiration (never expires)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Default Tier (when Founding Member is disabled) */}
      {!settings.founding_member_enabled && (
        <div className="bg-[#2D063E]/50 border border-[#4A0A66] rounded-xl p-5">
          <label className="text-sm text-[#FDF2F8]/70 block mb-2">Default Tier for New Users</label>
          <select
            value={settings.default_tier}
            onChange={(e) => setSettings(prev => ({ ...prev, default_tier: e.target.value }))}
            className="w-full max-w-xs bg-[#1a0527] border border-[#4A0A66] text-white rounded-lg px-4 py-2 focus:outline-none focus:border-[#df1283]"
          >
            <option value="free">Free</option>
            <option value="founding_member">Founding Member</option>
            <option value="premium">Premium</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <p className="text-xs text-[#FDF2F8]/40 mt-2">
            This tier will be assigned to all new users when they sign up.
          </p>
        </div>
      )}

      {/* Current Status */}
      <div className="bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg p-4">
        <div className="flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4 text-[#0FFFE2]" />
          <span className="text-[#FDF2F8]/70">
            <strong className="text-white">Current Status:</strong> New users signing up will receive{' '}
            <span className={settings.founding_member_enabled ? 'text-yellow-500 font-semibold' : 'text-[#df1283] font-semibold'}>
              {settings.founding_member_enabled ? 'Founding Member' : settings.default_tier.replace('_', ' ')}
            </span>
            {' '}tier
            {settings.founding_member_enabled && settings.founding_member_end_date && (
              <span className="text-[#FDF2F8]/50"> until {(() => {
                const d = new Date(settings.founding_member_end_date);
                const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'America/New_York' });
                const day = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'America/New_York' });
                const year = d.toLocaleDateString('en-US', { year: 'numeric', timeZone: 'America/New_York' });
                return `${month} ${day}, ${year} at 12:00 AM EST`;
              })()}</span>
            )}
          </span>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-[#4A0A66]">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white hover:opacity-90"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Upgrade Banner Messages Settings Component
export const UpgradeBannerSettings = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);

  const iconOptions = [
    { value: 'crown', label: 'Crown' },
    { value: 'sparkles', label: 'Sparkles' },
    { value: 'gift', label: 'Gift' },
    { value: 'zap', label: 'Lightning' },
    { value: 'star', label: 'Star' },
    { value: 'fire', label: 'Fire' },
    { value: 'heart', label: 'Heart' },
    { value: 'megaphone', label: 'Megaphone' },
  ];

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/settings/upgrade-messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMessage = () => {
    const newMessage = {
      id: `msg-${Date.now()}`,
      title: 'New Promo',
      subtitle: 'Limited time offer',
      description: '',
      icon: 'sparkles',
      ctaText: 'Upgrade Now',
      ctaAction: 'upgrade',
      ctaLink: '',
      gradientStart: '#df1283',
      gradientEnd: '#FF7901',
      discountBadge: '',
      priceText: 'Starting at $6.25/mo',
      is_active: true,
      order: messages.length,
    };
    setEditingMessage(newMessage);
  };

  const handleSaveMessage = async (message) => {
    setSaving(true);
    try {
      const existingIndex = messages.findIndex(m => m.id === message.id);
      let updatedMessages;
      
      if (existingIndex >= 0) {
        updatedMessages = [...messages];
        updatedMessages[existingIndex] = message;
      } else {
        updatedMessages = [...messages, message];
      }
      
      await axios.put(`${API_BASE}/api/admin/settings/upgrade-messages`, { messages: updatedMessages });
      setMessages(updatedMessages);
      setEditingMessage(null);
      toast.success('Message saved successfully');
    } catch (error) {
      toast.error('Failed to save message');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMessage = async (id) => {
    try {
      const updatedMessages = messages.filter(m => m.id !== id);
      await axios.put(`${API_BASE}/api/admin/settings/upgrade-messages`, { messages: updatedMessages });
      setMessages(updatedMessages);
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleToggleActive = async (id) => {
    const updatedMessages = messages.map(m => 
      m.id === id ? { ...m, is_active: !m.is_active } : m
    );
    try {
      await axios.put(`${API_BASE}/api/admin/settings/upgrade-messages`, { messages: updatedMessages });
      setMessages(updatedMessages);
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 text-[#df1283] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#FF7901]" />
            Upgrade Banner Messages
          </h3>
          <p className="text-sm text-[#FDF2F8]/60 mt-1">
            Configure rotating messages for the upgrade banner (shown to free users)
          </p>
        </div>
        <Button
          onClick={handleAddMessage}
          className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white"
        >
          Add Message
        </Button>
      </div>

      {/* Messages List */}
      {messages.length === 0 ? (
        <div className="text-center py-8 text-[#FDF2F8]/50">
          <p>No custom messages configured.</p>
          <p className="text-sm mt-1">The default "Unlock Premium" message will be shown.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                msg.is_active 
                  ? 'bg-[#2D063E]/50 border-[#df1283]/50' 
                  : 'bg-[#1a0527]/50 border-[#4A0A66]/50 opacity-60'
              }`}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${msg.gradientStart}, ${msg.gradientEnd})` }}
              >
                <span className="text-white text-lg">
                  {msg.icon === 'crown' && '👑'}
                  {msg.icon === 'sparkles' && '✨'}
                  {msg.icon === 'gift' && '🎁'}
                  {msg.icon === 'zap' && '⚡'}
                  {msg.icon === 'star' && '⭐'}
                  {msg.icon === 'fire' && '🔥'}
                  {msg.icon === 'heart' && '❤️'}
                  {msg.icon === 'megaphone' && '📣'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-white truncate">{msg.title}</h4>
                <p className="text-sm text-[#FDF2F8]/60 truncate">{msg.subtitle}</p>
              </div>
              {msg.discountBadge && (
                <span className="px-2 py-1 text-xs font-bold rounded-full bg-green-500/20 text-green-400">
                  {msg.discountBadge}
                </span>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleActive(msg.id)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    msg.is_active ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    msg.is_active ? 'left-7' : 'left-1'
                  }`} />
                </button>
                <Button
                  onClick={() => setEditingMessage(msg)}
                  size="sm"
                  variant="ghost"
                  className="text-[#FDF2F8]/60 hover:text-white"
                >
                  Edit
                </Button>
                <Button
                  onClick={() => handleDeleteMessage(msg.id)}
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingMessage && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-white mb-4">
              {messages.find(m => m.id === editingMessage.id) ? 'Edit Message' : 'New Message'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Title</label>
                <Input
                  value={editingMessage.title}
                  onChange={(e) => setEditingMessage(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  placeholder="e.g., 50% Off Premium!"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Subtitle</label>
                <Input
                  value={editingMessage.subtitle}
                  onChange={(e) => setEditingMessage(prev => ({ ...prev, subtitle: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  placeholder="e.g., Limited time offer"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Icon</label>
                <select
                  value={editingMessage.icon}
                  onChange={(e) => setEditingMessage(prev => ({ ...prev, icon: e.target.value }))}
                  className="w-full bg-[#1a0527] border border-[#4A0A66] text-white rounded-md p-2"
                >
                  {iconOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#FDF2F8]/70 mb-1">Gradient Start</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={editingMessage.gradientStart}
                      onChange={(e) => setEditingMessage(prev => ({ ...prev, gradientStart: e.target.value }))}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={editingMessage.gradientStart}
                      onChange={(e) => setEditingMessage(prev => ({ ...prev, gradientStart: e.target.value }))}
                      className="bg-[#1a0527] border-[#4A0A66] text-white flex-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#FDF2F8]/70 mb-1">Gradient End</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={editingMessage.gradientEnd}
                      onChange={(e) => setEditingMessage(prev => ({ ...prev, gradientEnd: e.target.value }))}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={editingMessage.gradientEnd}
                      onChange={(e) => setEditingMessage(prev => ({ ...prev, gradientEnd: e.target.value }))}
                      className="bg-[#1a0527] border-[#4A0A66] text-white flex-1"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">CTA Button Text</label>
                <Input
                  value={editingMessage.ctaText}
                  onChange={(e) => setEditingMessage(prev => ({ ...prev, ctaText: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  placeholder="e.g., Get 50% Off"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Discount Badge (optional)</label>
                <Input
                  value={editingMessage.discountBadge}
                  onChange={(e) => setEditingMessage(prev => ({ ...prev, discountBadge: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  placeholder="e.g., 50% OFF"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Price Text</label>
                <Input
                  value={editingMessage.priceText}
                  onChange={(e) => setEditingMessage(prev => ({ ...prev, priceText: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  placeholder="e.g., Now just $4.99/mo"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#4A0A66]">
              <Button
                onClick={() => setEditingMessage(null)}
                variant="ghost"
                className="text-[#FDF2F8]/60"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleSaveMessage(editingMessage)}
                disabled={saving}
                className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white"
              >
                {saving ? 'Saving...' : 'Save Message'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="p-4 bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg">
        <p className="text-sm text-[#FDF2F8]/70">
          <strong className="text-[#0FFFE2]">How it works:</strong> Active messages will rotate automatically 
          every 5 seconds on the upgrade banner. Users can also navigate manually using the arrows.
        </p>
      </div>
    </div>
  );
};

// Sortable Referral Option Item Component with visibility toggle and user count
const SortableReferralItem = ({ 
  id, 
  option,  // Now an object: { name, active, user_count }
  index, 
  editingIndex, 
  editingValue, 
  setEditingValue,
  handleStartEdit, 
  handleSaveEdit, 
  handleCancelEdit, 
  handleRemoveOption,
  handleToggleVisibility
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const isActive = option.active !== false;
  const userCount = option.user_count || 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-3 border rounded-lg group transition-all ${
        isDragging ? 'shadow-lg ring-2 ring-[#df1283]' : ''
      } ${
        isActive 
          ? 'bg-[#2D063E] border-[#4A0A66]' 
          : 'bg-[#1a0527]/50 border-[#4A0A66]/50'
      }`}
      data-testid={`referral-option-item-${index}`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="p-1 rounded cursor-grab active:cursor-grabbing text-[#FDF2F8]/40 hover:text-[#0FFFE2] hover:bg-[#0FFFE2]/20 touch-none"
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      
      {/* Visibility Toggle */}
      <button
        onClick={() => handleToggleVisibility(index)}
        className={`p-1.5 rounded transition-colors ${
          isActive 
            ? 'text-[#0FFFE2] hover:bg-[#0FFFE2]/20' 
            : 'text-[#FDF2F8]/30 hover:text-[#FDF2F8]/50 hover:bg-[#FDF2F8]/10'
        }`}
        title={isActive ? 'Click to hide from new users' : 'Click to show to new users'}
      >
        {isActive ? (
          <Eye className="w-4 h-4" />
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
          </svg>
        )}
      </button>
      
      <span className="text-[#FDF2F8]/40 text-sm w-6">{index + 1}.</span>
      
      {editingIndex === index ? (
        <div className="flex-1 flex items-center gap-2">
          <Input
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveEdit();
              if (e.key === 'Escape') handleCancelEdit();
            }}
            className="bg-[#1a0527] border-[#4A0A66] text-white flex-1"
            autoFocus
          />
          <button
            onClick={handleSaveEdit}
            className="p-1.5 rounded text-[#22c55e] hover:bg-[#22c55e]/20"
            title="Save"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={handleCancelEdit}
            className="p-1.5 rounded text-[#FDF2F8]/40 hover:text-red-400 hover:bg-red-500/20"
            title="Cancel"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : (
        <>
          <span 
            className={`flex-1 cursor-pointer transition-colors ${
              isActive 
                ? 'text-white hover:text-[#0FFFE2]' 
                : 'text-[#FDF2F8]/40 line-through'
            }`}
            onClick={() => handleStartEdit(index)}
            title="Click to edit"
          >
            {option.name}
          </span>
          
          {/* User Count Badge */}
          <span 
            className={`text-xs px-2 py-0.5 rounded-full ${
              userCount > 0 
                ? 'bg-[#df1283]/20 text-[#df1283]' 
                : 'bg-[#4A0A66]/30 text-[#FDF2F8]/40'
            }`}
            title={`${userCount} user${userCount !== 1 ? 's' : ''} selected this option`}
          >
            {userCount} {userCount === 1 ? 'user' : 'users'}
          </span>
          
          <button
            onClick={() => handleStartEdit(index)}
            className="p-1.5 rounded text-[#FDF2F8]/40 hover:text-[#0FFFE2] hover:bg-[#0FFFE2]/20 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Edit"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          
          <button
            onClick={() => handleRemoveOption(index)}
            className={`p-1.5 rounded transition-opacity ${
              userCount > 0 
                ? 'text-[#FDF2F8]/20 cursor-not-allowed' 
                : 'text-[#FDF2F8]/40 hover:text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100'
            }`}
            title={userCount > 0 ? `Cannot delete - ${userCount} users have selected this` : 'Delete option'}
            disabled={userCount > 0}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
};

// Referral Sources Settings Component
export const ReferralSourcesSettings = () => {
  const [options, setOptions] = useState([]);  // Now array of {name, active, user_count}
  const [originalOptions, setOriginalOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [newOption, setNewOption] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  useEffect(() => {
    fetchReferralOptions();
  }, []);

  useEffect(() => {
    if (originalOptions.length > 0) {
      // Compare only name and active fields for changes (not user_count)
      const optionsForCompare = options.map(o => ({ name: o.name, active: o.active }));
      const originalForCompare = originalOptions.map(o => ({ name: o.name, active: o.active }));
      const changed = JSON.stringify(optionsForCompare) !== JSON.stringify(originalForCompare);
      setHasChanges(changed);
    }
  }, [options, originalOptions]);

  const fetchReferralOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/settings/referral-options`);
      if (response.data.options) {
        // Handle both old format (strings) and new format (objects)
        let opts = response.data.options;
        
        // Convert string format to object format if needed
        if (opts.length > 0 && typeof opts[0] === 'string') {
          opts = opts.map(name => ({ name, active: true, user_count: 0 }));
        }
        
        // Remove "Other" from the list for editing (it's always added at the end)
        opts = opts.filter(o => o.name !== 'Other');
        setOptions(opts);
        setOriginalOptions(JSON.parse(JSON.stringify(opts)));
      }
    } catch (error) {
      console.error('Failed to fetch referral options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Send options in new format (backend will add "Other" at the end)
      await axios.put(`${API_BASE}/api/admin/settings/referral-options`, { options });
      setOriginalOptions(JSON.parse(JSON.stringify(options)));
      setHasChanges(false);
      toast.success('Referral options saved successfully');
    } catch (error) {
      toast.error('Failed to save referral options');
    } finally {
      setSaving(false);
    }
  };

  const handleAddOption = () => {
    if (!newOption.trim()) return;
    if (options.some(o => o.name === newOption.trim())) {
      toast.error('This option already exists');
      return;
    }
    setOptions([...options, { name: newOption.trim(), active: true, user_count: 0 }]);
    setNewOption('');
  };

  const handleRemoveOption = (index) => {
    const option = options[index];
    if (option.user_count > 0) {
      toast.error(`Cannot delete - ${option.user_count} users have selected this option. Hide it instead.`);
      return;
    }
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleToggleVisibility = (index) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], active: !newOptions[index].active };
    setOptions(newOptions);
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setOptions((items) => {
        const oldIndex = items.findIndex((item, idx) => `referral-${idx}` === active.id);
        const newIndex = items.findIndex((item, idx) => `referral-${idx}` === over.id);
        
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditingValue(options[index].name);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    if (!editingValue.trim()) {
      toast.error('Option cannot be empty');
      return;
    }
    // Check for duplicates (excluding current index)
    if (options.some((opt, i) => i !== editingIndex && opt.name === editingValue.trim())) {
      toast.error('This option already exists');
      return;
    }
    const newOptions = [...options];
    newOptions[editingIndex] = { ...newOptions[editingIndex], name: editingValue.trim() };
    setOptions(newOptions);
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 text-[#df1283] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-[#0FFFE2]" />
            Referral Sources
          </h3>
          <p className="text-sm text-[#FDF2F8]/60 mt-1">
            Manage the "How did you hear about us?" dropdown options shown during onboarding
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`flex items-center gap-2 ${
            hasChanges 
              ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white' 
              : 'bg-[#4A0A66]/50 text-[#FDF2F8]/50'
          }`}
          data-testid="save-referral-options-btn"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </Button>
      </div>

      {/* Add New Option */}
      <div className="flex gap-2">
        <Input
          value={newOption}
          onChange={(e) => setNewOption(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddOption()}
          placeholder="Add new referral source..."
          className="bg-[#1a0527] border-[#4A0A66] text-white flex-1"
          data-testid="new-referral-option-input"
        />
        <Button
          onClick={handleAddOption}
          disabled={!newOption.trim()}
          className="bg-[#22c55e] hover:bg-[#16a34a] text-white"
          data-testid="add-referral-option-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </div>

      {/* Options List with Drag and Drop */}
      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={options.map((_, idx) => `referral-${idx}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
            {options.map((option, index) => (
              <SortableReferralItem
                key={`referral-${index}`}
                id={`referral-${index}`}
                option={option}
                index={index}
                editingIndex={editingIndex}
                editingValue={editingValue}
                setEditingValue={setEditingValue}
                handleStartEdit={handleStartEdit}
                handleSaveEdit={handleSaveEdit}
                handleCancelEdit={handleCancelEdit}
                handleRemoveOption={handleRemoveOption}
                handleToggleVisibility={handleToggleVisibility}
              />
            ))}
            
            {/* "Other" option (always at end, not editable) */}
            <div className="flex items-center gap-2 p-3 bg-[#1a0527]/50 border border-[#4A0A66]/50 rounded-lg">
              <div className="w-6" /> {/* Spacer for drag handle */}
              <Eye className="w-4 h-4 text-[#0FFFE2]/50" /> {/* Always visible */}
              <span className="text-[#FDF2F8]/40 text-sm w-6">{options.length + 1}.</span>
              <span className="text-[#FDF2F8]/50 flex-1 italic">Other</span>
              <span className="text-xs text-[#FDF2F8]/30">(always shown)</span>
            </div>
          </div>
        </SortableContext>
      </DndContext>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-[#FDF2F8]/60">
        <div className="flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5 text-[#0FFFE2]" />
          <span>Visible to new users</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-[#FDF2F8]/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
          </svg>
          <span>Hidden (existing data preserved)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="px-1.5 py-0.5 bg-[#df1283]/20 text-[#df1283] rounded text-[10px]">N users</span>
          <span>Users who selected this</span>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg">
        <p className="text-sm text-[#FDF2F8]/70">
          <strong className="text-[#0FFFE2]">Tip:</strong> Click the <Eye className="w-4 h-4 inline mx-1" /> icon to show/hide options for new users. 
          Hidden options preserve existing user data. Drag <GripVertical className="w-4 h-4 inline mx-1" /> to reorder. 
          Options with users cannot be deleted (hide them instead).
        </p>
      </div>
    </div>
  );
};

// Onboarding Management Component
export const OnboardingManagement = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // User list modal state
  const [showUserListModal, setShowUserListModal] = useState(false);
  const [userListType, setUserListType] = useState(null); // 'pending' or 'no_referral'
  const [userList, setUserList] = useState([]);
  const [loadingUserList, setLoadingUserList] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/users/onboarding-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch onboarding stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUserList = async (type) => {
    setLoadingUserList(true);
    setUserListType(type);
    setShowUserListModal(true);
    try {
      const response = await axios.get(`${API_BASE}/api/admin/users/onboarding-list?type=${type}`);
      setUserList(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch user list:', error);
      toast.error('Failed to load user list');
      setUserList([]);
    } finally {
      setLoadingUserList(false);
    }
  };

  const handleRequireOnboarding = async (mode) => {
    const confirmMessages = {
      missing_referral: `This will require onboarding for ${stats?.missing_referral_data || 0} users who haven't provided referral data. Continue?`,
      all: `WARNING: This will require ALL ${stats?.total_users || 0} users to complete onboarding again. Are you sure?`
    };
    
    if (!confirm(confirmMessages[mode])) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API_BASE}/api/admin/users/require-onboarding`, { mode });
      toast.success(response.data.message);
      fetchStats(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update users');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 text-[#df1283] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-[#22c55e]" />
            Onboarding Management
          </h3>
          <p className="text-sm text-[#FDF2F8]/60 mt-1">
            Require existing users to complete onboarding when they log in next
          </p>
        </div>
        <Button
          onClick={fetchStats}
          variant="ghost"
          className="text-[#FDF2F8]/60 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-[#2D063E] border border-[#4A0A66] rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-[#FDF2F8]/50" />
            <span className="text-sm text-[#FDF2F8]/50">Total Users</span>
          </div>
          <span className="text-2xl font-bold text-white">{stats?.total_users || 0}</span>
        </div>
        
        <div className="p-4 bg-[#2D063E] border border-[#4A0A66] rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <UserCheck className="w-4 h-4 text-[#22c55e]" />
            <span className="text-sm text-[#FDF2F8]/50">Completed</span>
          </div>
          <span className="text-2xl font-bold text-[#22c55e]">{stats?.completed_onboarding || 0}</span>
        </div>
        
        {/* Clickable Pending Card */}
        <button
          onClick={() => fetchUserList('pending')}
          disabled={!stats?.pending_onboarding}
          className="p-4 bg-[#2D063E] border border-[#4A0A66] rounded-lg text-left hover:border-[#f59e0b]/50 hover:bg-[#f59e0b]/5 transition-colors disabled:cursor-default disabled:hover:border-[#4A0A66] disabled:hover:bg-[#2D063E]"
          data-testid="pending-users-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <UserX className="w-4 h-4 text-[#f59e0b]" />
            <span className="text-sm text-[#FDF2F8]/50">Pending</span>
            {stats?.pending_onboarding > 0 && (
              <span className="text-xs text-[#f59e0b] ml-auto">Click to view →</span>
            )}
          </div>
          <span className="text-2xl font-bold text-[#f59e0b]">{stats?.pending_onboarding || 0}</span>
        </button>
        
        {/* Clickable No Referral Data Card */}
        <button
          onClick={() => fetchUserList('no_referral')}
          disabled={!stats?.missing_referral_data}
          className="p-4 bg-[#2D063E] border border-[#4A0A66] rounded-lg text-left hover:border-[#ef4444]/50 hover:bg-[#ef4444]/5 transition-colors disabled:cursor-default disabled:hover:border-[#4A0A66] disabled:hover:bg-[#2D063E]"
          data-testid="no-referral-users-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-[#ef4444]" />
            <span className="text-sm text-[#FDF2F8]/50">No Referral Data</span>
            {stats?.missing_referral_data > 0 && (
              <span className="text-xs text-[#ef4444] ml-auto">Click to view →</span>
            )}
          </div>
          <span className="text-2xl font-bold text-[#ef4444]">{stats?.missing_referral_data || 0}</span>
        </button>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          onClick={() => handleRequireOnboarding('missing_referral')}
          disabled={processing || (stats?.missing_referral_data === 0)}
          className="w-full justify-start gap-3 bg-[#f59e0b]/20 hover:bg-[#f59e0b]/30 text-[#f59e0b] border border-[#f59e0b]/30"
          data-testid="require-onboarding-missing-btn"
        >
          {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
          Require Onboarding for Users Missing Referral Data ({stats?.missing_referral_data || 0} users)
        </Button>
        
        <Button
          onClick={() => handleRequireOnboarding('all')}
          disabled={processing}
          className="w-full justify-start gap-3 bg-[#ef4444]/20 hover:bg-[#ef4444]/30 text-[#ef4444] border border-[#ef4444]/30"
          data-testid="require-onboarding-all-btn"
        >
          {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
          Require Onboarding for ALL Users (Reset Everyone)
        </Button>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg">
        <p className="text-sm text-[#FDF2F8]/70">
          <strong className="text-[#0FFFE2]">How it works:</strong> When you click a button above, 
          affected users will be redirected to the onboarding page the next time they log in. 
          They must complete onboarding (including selecting a referral source) before accessing the app.
        </p>
      </div>
      
      {/* User List Modal */}
      {showUserListModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={() => setShowUserListModal(false)}
          data-testid="onboarding-user-list-modal"
        >
          <div 
            className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl border border-[#4A0A66] w-full max-w-2xl max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                {userListType === 'pending' ? (
                  <>
                    <UserX className="w-5 h-5 text-[#f59e0b]" />
                    Users Pending Onboarding
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-[#ef4444]" />
                    Users Missing Referral Data
                  </>
                )}
              </h3>
              <button
                onClick={() => setShowUserListModal(false)}
                className="p-2 rounded-lg hover:bg-[#4A0A66]/50 text-[#FDF2F8]/70 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {loadingUserList ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-[#df1283]" />
                  <p className="text-[#FDF2F8]/50">Loading users...</p>
                </div>
              ) : userList.length === 0 ? (
                <div className="text-center py-12 text-[#FDF2F8]/50">
                  No users found
                </div>
              ) : (
                <div className="space-y-2">
                  {userList.map((user, idx) => (
                    <div 
                      key={user.user_id || idx} 
                      className="flex items-center justify-between p-3 bg-[#1a0527] rounded-lg border border-[#4A0A66]/30"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#df1283] to-[#FF7901] flex items-center justify-center text-white font-bold">
                          {(user.first_name?.[0] || user.email?.[0] || '?').toUpperCase()}
                        </div>
                        <div>
                          <div className="text-white font-medium">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}` 
                              : user.first_name || user.email?.split('@')[0] || 'Unknown'}
                          </div>
                          <div className="text-[#FDF2F8]/50 text-sm">{user.email}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        {user.created_at && (
                          <div className="text-xs text-[#FDF2F8]/40">
                            Joined: {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        )}
                        {user.last_login && (
                          <div className="text-xs text-[#FDF2F8]/40">
                            Last login: {new Date(user.last_login).toLocaleDateString()}
                          </div>
                        )}
                        {userListType === 'no_referral' && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-[#ef4444]/20 text-[#ef4444] rounded text-xs">
                            No referral source
                          </span>
                        )}
                        {userListType === 'pending' && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-[#f59e0b]/20 text-[#f59e0b] rounded text-xs">
                            Onboarding pending
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Re-export DashboardDisplaySettings from its own file
export { default as DashboardDisplaySettings } from './DashboardDisplaySettings';

// Re-export TierEditor from its own file
export { default as TierEditor } from './TierEditor';

// Landing Page Settings Component
export const LandingPageSettings = () => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/admin/settings/landing-page`, {
          withCredentials: true
        });
        setSettings(response.data.config);
      } catch (err) {
        console.error('Error fetching landing page settings:', err);
        // Use defaults
        setSettings({
          hero_button: {
            text: "Become a Founding Member Free",
            fallback_text: "Become a Member Today",
            action: "scroll_to_pricing",
            external_url: ""
          },
          secondary_button: {
            text: "Learn More",
            action: "scroll_to_features",
            external_url: ""
          },
          video: {
            enabled: true,
            youtube_url: "https://www.youtube.com/embed/YyiNzi0bciA?rel=0",
            label: "Watch a quick walkthrough of your journal"
          }
        });
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [API_BASE]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/admin/settings/landing-page`, {
        config: settings
      }, {
        withCredentials: true
      });
      toast.success('Landing page settings saved!');
    } catch (err) {
      console.error('Error saving landing page settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateHeroButton = (key, value) => {
    setSettings(prev => ({
      ...prev,
      hero_button: { ...prev.hero_button, [key]: value }
    }));
  };

  const updateSecondaryButton = (key, value) => {
    setSettings(prev => ({
      ...prev,
      secondary_button: { ...prev.secondary_button, [key]: value }
    }));
  };

  const updateVideo = (key, value) => {
    setSettings(prev => ({
      ...prev,
      video: { ...prev.video, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-[#df1283]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Button Settings */}
      <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Star className="w-4 h-4 text-[#FF7901]" />
          Primary Button (Hero)
        </h4>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-[#FDF2F8]/60 block mb-1">Button Text (when Founding Member available)</label>
            <Input
              value={settings?.hero_button?.text || ''}
              onChange={(e) => updateHeroButton('text', e.target.value)}
              placeholder="Become a Founding Member Free"
              className="bg-[#2D063E] border-[#4A0A66] text-white"
            />
          </div>
          <div>
            <label className="text-xs text-[#FDF2F8]/60 block mb-1">Fallback Text (when Founding Member ends)</label>
            <Input
              value={settings?.hero_button?.fallback_text || ''}
              onChange={(e) => updateHeroButton('fallback_text', e.target.value)}
              placeholder="Become a Member Today"
              className="bg-[#2D063E] border-[#4A0A66] text-white"
            />
          </div>
          <div>
            <label className="text-xs text-[#FDF2F8]/60 block mb-1">Action</label>
            <select
              value={settings?.hero_button?.action || 'scroll_to_pricing'}
              onChange={(e) => updateHeroButton('action', e.target.value)}
              className="w-full bg-[#2D063E] border border-[#4A0A66] text-white rounded-md p-2"
            >
              <option value="scroll_to_pricing">Scroll to Pricing</option>
              <option value="scroll_to_features">Scroll to Features</option>
              <option value="external_link">External Link</option>
            </select>
          </div>
          {settings?.hero_button?.action === 'external_link' && (
            <div>
              <label className="text-xs text-[#FDF2F8]/60 block mb-1">External URL</label>
              <Input
                value={settings?.hero_button?.external_url || ''}
                onChange={(e) => updateHeroButton('external_url', e.target.value)}
                placeholder="https://..."
                className="bg-[#2D063E] border-[#4A0A66] text-white"
              />
            </div>
          )}
        </div>
      </div>

      {/* Secondary Button Settings */}
      <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <LinkIcon className="w-4 h-4 text-[#0FFFE2]" />
          Secondary Button
        </h4>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-[#FDF2F8]/60 block mb-1">Button Text</label>
            <Input
              value={settings?.secondary_button?.text || ''}
              onChange={(e) => updateSecondaryButton('text', e.target.value)}
              placeholder="Learn More"
              className="bg-[#2D063E] border-[#4A0A66] text-white"
            />
          </div>
          <div>
            <label className="text-xs text-[#FDF2F8]/60 block mb-1">Action</label>
            <select
              value={settings?.secondary_button?.action || 'scroll_to_features'}
              onChange={(e) => updateSecondaryButton('action', e.target.value)}
              className="w-full bg-[#2D063E] border border-[#4A0A66] text-white rounded-md p-2"
            >
              <option value="scroll_to_features">Scroll to Features</option>
              <option value="scroll_to_pricing">Scroll to Pricing</option>
              <option value="external_link">External Link</option>
            </select>
          </div>
          {settings?.secondary_button?.action === 'external_link' && (
            <div>
              <label className="text-xs text-[#FDF2F8]/60 block mb-1">External URL</label>
              <Input
                value={settings?.secondary_button?.external_url || ''}
                onChange={(e) => updateSecondaryButton('external_url', e.target.value)}
                placeholder="https://..."
                className="bg-[#2D063E] border-[#4A0A66] text-white"
              />
            </div>
          )}
        </div>
      </div>

      {/* Video Settings */}
      <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Eye className="w-4 h-4 text-[#df1283]" />
          Video Section
        </h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#FDF2F8]/80">Show Video</span>
            <button
              onClick={() => updateVideo('enabled', !settings?.video?.enabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                settings?.video?.enabled ? 'bg-[#0FFFE2]' : 'bg-[#4A0A66]'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                settings?.video?.enabled ? 'translate-x-7' : 'translate-x-1'
              }`} />
            </button>
          </div>
          <div>
            <label className="text-xs text-[#FDF2F8]/60 block mb-1">YouTube Embed URL</label>
            <Input
              value={settings?.video?.youtube_url || ''}
              onChange={(e) => updateVideo('youtube_url', e.target.value)}
              placeholder="https://www.youtube.com/embed/VIDEO_ID"
              className="bg-[#2D063E] border-[#4A0A66] text-white"
              disabled={!settings?.video?.enabled}
            />
            <p className="text-xs text-[#FDF2F8]/40 mt-1">
              Use the embed URL format: https://www.youtube.com/embed/VIDEO_ID?rel=0
            </p>
          </div>
          <div>
            <label className="text-xs text-[#FDF2F8]/60 block mb-1">Video Label</label>
            <Input
              value={settings?.video?.label || ''}
              onChange={(e) => updateVideo('label', e.target.value)}
              placeholder="Watch a quick walkthrough"
              className="bg-[#2D063E] border-[#4A0A66] text-white"
              disabled={!settings?.video?.enabled}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>
      
      <p className="text-xs text-[#FDF2F8]/40 text-center">
        Changes will take effect immediately on the landing page after saving. You may need to redeploy to see changes in production.
      </p>
    </div>
  );
};

export default { DocumentationHub, TierLabelsSettings, UpgradeBannerSettings, ReferralSourcesSettings, OnboardingManagement, LandingPageSettings };
